/**
 * Member-portal side of in-app calls (TRUS-668). Members can only JOIN calls
 * staff start in their own member DM — no Start buttons anywhere here.
 * Everything below uses memberCallsApi (member token), never the staff client.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone, Video as VideoIcon } from 'lucide-react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { memberCallsApi } from '@/services/callsApi';
import { Button } from '../../ui/button';
import { callStore, useCallStore } from './callStore';
import { CallsApiContext, useCallsApi, useCallsConfig, useDocumentVisible } from './useCalls';
import { CallsHost } from './CallsHost';

/** Provides the member API to call components, and mounts the banner + panel. */
export function MemberCallsProvider({ children }: { children: React.ReactNode }) {
  return (
    <CallsApiContext.Provider value={memberCallsApi}>
      {children}
      <CallsHost />
    </CallsApiContext.Provider>
  );
}

/** "channel:700" → 700 (member DMs are channel threads). */
export function channelIdFromThreadKey(threadKey: string | null | undefined): number | null {
  const m = /^channel:(\d+)$/.exec(threadKey || '');
  return m ? Number(m[1]) : null;
}

/** A green "Join call" in the open DM's header while a call is active in it. */
export function MemberThreadJoinButton({ channelId, channelName }: { channelId: number; channelName?: string }) {
  const { t } = useAppTranslation();
  const api = useCallsApi();
  const { config } = useCallsConfig();
  const { session } = useCallStore();
  const visible = useDocumentVisible();
  const q = useQuery({
    queryKey: ['calls-active-channel', api.kind, channelId],
    queryFn: async () => {
      try {
        return await api.active(channelId);
      } catch {
        return [];
      }
    },
    enabled: config.enabled,
    refetchInterval: config.enabled && visible ? 5000 : false,
    retry: false,
  });
  const call = (q.data ?? []).find((c) => c.status === 'active');
  if (!config.enabled || !call || session?.callId === call.id) return null;
  return (
    <Button
      type="button"
      size="sm"
      className="gap-1 bg-emerald-600 text-white hover:bg-emerald-700"
      onClick={() =>
        callStore.open({
          callId: call.id,
          channelId,
          mode: call.mode,
          channelName,
          // A recording call (TRUS-690: orgs that record calls with members): ask first.
          consentNeeded: !!call.recording && !call.my_recording_consent,
        })
      }
      data-testid="member-join-call"
    >
      {call.mode === 'video' ? <VideoIcon className="h-4 w-4" /> : <Phone className="h-4 w-4" />}
      {t('messaging.calls.joinCall', 'Join call')}
    </Button>
  );
}
